import service from '@/services';
import { getUserData } from './custom';

export type ContactOccupation = {
  name?: string;
  start_date?: string;
};

export type DriversLicense = {
  number?: string;
  state?: string;
  issued_date?: string;
  expires_date?: string;
};

export type HouseholdRequest = {
  name?: string;
  title?: string;
};

export type MailingAddressRequest = {
  street_line_1?: string;
  street_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country: string;
  principal?: boolean;
  kind?: string;
  destroy?: boolean;
};

export type EmailAddressRequest = {
  address: string;
  principal?: boolean;
  kind?: string;
  destroy?: boolean;
};

export type PhoneNumberRequest = {
  address: string;
  principal?: boolean;
  extension?: string;
  kind?: string;
  destroy?: boolean;
};

export type WebsiteRequest = {
  address: string;
  principal?: boolean;
  kind?: string;
  destroy?: boolean;
};

export type CustomFieldRequest = {
  id: number;
  value: string;
};

export type ContactRoleValueRequest = {
  id: number;
  value: number;
};

export type CreateContactRequest = {
  prefix?: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  suffix?: string;
  nickname?: string;
  job_title?: string;
  company_name?: string;
  twitter_name?: string;
  linkedin_url?: string;
  background_information?: string;
  birth_date?: string;
  anniversary?: string;
  client_since?: string;
  date_of_death?: string;
  assigned_to?: number;
  referred_by?: number;
  type?: string;
  gender?: string;
  contact_source?: string;
  contact_type?: string;
  status?: string;
  marital_status?: string;
  attorney?: number;
  cpa?: number;
  doctor?: number;
  insurance?: number;
  business_manager?: number;
  family_officer?: number;
  assistant?: number;
  other?: number;
  trusted_contact?: number;
  important_information?: string;
  personal_interests?: string;
  investment_objective?: string;
  time_horizon?: string;
  risk_tolerance?: string;
  mutual_fund_experience?: number;
  stocks_and_bonds_experience?: number;
  partnerships_experience?: number;
  other_investing_experience?: number;
  gross_annual_income?: number;
  assets?: number;
  non_liquid_assets?: number;
  liabilities?: number;
  adjusted_gross_income?: number;
  estimated_taxes?: number;
  confirmed_by_tax_return?: boolean;
  tax_year?: number;
  tax_bracket?: number;
  birth_place?: string;
  maiden_name?: string;
  passport_number?: string;
  green_card_number?: string;
  occupation?: ContactOccupation;
  drivers_license?: DriversLicense;
  retirement_date?: string;
  signed_fee_agreement_date?: string;
  signed_ips_agreement_date?: string;
  signed_fp_agreement_date?: string;
  last_adv_offering_date?: string;
  initial_crs_offering_date?: string;
  last_crs_offering_date?: string;
  last_privacy_offering_date?: string;
  company?: string;
  household?: HouseholdRequest;
  tags?: string[];
  street_addresses?: MailingAddressRequest[];
  email_addresses?: EmailAddressRequest[];
  phone_numbers?: PhoneNumberRequest[];
  websites?: WebsiteRequest[];
  custom_fields?: CustomFieldRequest[];
  contact_roles?: ContactRoleValueRequest[];
  visible_to?: string;
};

export const getInitiateWealthboxOauthUrl = async () => {
  const user = getUserData();
  const url = `/client/${user.client_id}/wealthbox-oauth`;
  try {
    const response = await service.post<string>(url);
    return response;
  } catch (error) {
    console.error('Failed to initiate oauth', error);
    throw error;
  }
};

export type WealthboxStatus = {
  status: 'authorized' | 'expired' | 'not_authorized';
  expires_at: string;
  scope: string;
  message: string;
};

export const getWealthboxStatus = async () => {
  const user = getUserData();
  const url = `/client/${user.client_id}/wealthbox`;
  try {
    const response = await service.getByParams<WealthboxStatus>(url);
    return response;
  } catch (error) {
    console.error('Failed to initiate oauth', error);
    throw error;
  }
};

export type WealthboxContacts = {
  contacts: Array<Record<string, any>>;
};

export const getWealthboxContacts = async () => {
  const user = getUserData();
  const url = `/client/${user.client_id}/wealthbox/contacts`;
  try {
    const response = await service.getByParams<WealthboxContacts>(url);
    return response;
  } catch (error) {
    console.error('Failed to get wealthbox contacts', error);
    throw error;
  }
};

export const getWealthboxContactById = async (contactId: string) => {
  const user = getUserData();
  const url = `/client/${user.client_id}/wealthbox/contacts/${contactId}`;
  try {
    const response = await service.getByParams<Record<string, any>>(url);
    return response;
  } catch (error) {
    console.error('Failed to get wealthbox contact', error);
    throw error;
  }
};

export const createWealthboxContacts = async (createContactRequest: CreateContactRequest) => {
  const user = getUserData();
  const url = `/client/${user.client_id}/wealthbox/contacts`;
  try {
    const response = await service.post(url, createContactRequest);
    return response;
  } catch (error) {
    console.error('Failed to create wealthbox contact', error);
    throw error;
  }
};

export type WealthboxInvestorContactRelationship = {
  client_id: string;
  customer_id: string;
  contact_id: string;
};

export const getWealthboxInvestorContactRelationships = async () => {
  const user = getUserData();
  const url = `/client/${user.client_id}/wealthbox/customer-contact-relationships`;
  try {
    const response = await service.getByParams<WealthboxInvestorContactRelationship[]>(url);
    return response;
  } catch (error) {
    console.error('Failed to get wealthbox investor contact relationships', error);
    throw error;
  }
};

export const createWealthboxInvestorContactRelationship = async (
  investorId: string,
  contact_id: string,
) => {
  const user = getUserData();
  const url = `/client/${user.client_id}/wealthbox/customer-contact-relationships`;
  try {
    const response = await service.post(url, {
      customer_id: investorId,
      contact_id,
    });
    return response;
  } catch (error) {
    console.error('Failed to create wealthbox investor contact relationship', error);
    throw error;
  }
};

export const getWealthboxInvestorContactRelationship = async (investorId: string) => {
  const user = getUserData();
  const url = `/client/${user.client_id}/wealthbox/customer-contact-relationships/${investorId}`;
  try {
    const response = await service.getByParams<WealthboxInvestorContactRelationship | null>(url);
    return response;
  } catch (error) {
    console.error('Failed to get wealthbox investor contact relationship', error);
    throw error;
  }
};
